"use client"

import { useEffect, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { Card, CardContent } from "@/components/ui/card"
import { fetchCategories } from "@/lib/utils"

export default function CategoryGrid() {
  const [categories, setCategories] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
    fetchCategories(1, 8)
      .then((data) => {
        // Map API fields to frontend fields
        const mappedCategories = (data.data || data || []).map((c: any) => ({
          name: c.name || c.category_name || c.title || '',
          image: c.image || "/banners/no-img.jpg",
          href: `/category/${c.id}`,
          count: c.products_count ? `${c.products_count} products` : '',
          ...c,
        }))
        setCategories(mappedCategories)
        setLoading(false)
      })
      .catch((err) => {
        setError("Failed to load categories.")
        setLoading(false)
      })
  }, [])

  // Category name to image mapping
  const categoryImages: Record<string, string> = {
    "MUM AND BABY": "https://images.pexels.com/photos/415824/pexels-photo-415824.jpeg",
    "PERSONAL CARE": "https://images.pexels.com/photos/260405/pexels-photo-260405.jpeg",
    "VITAMINS AND SUPPLEMENTS": "https://images.pexels.com/photos/208518/pexels-photo-208518.jpeg",
    "HEALTHCARE": "https://images.pexels.com/photos/2383010/pexels-photo-2383010.jpeg",
    "BEAUTY AND MAKEUP": "https://images.pexels.com/photos/6045170/pexels-photo-6045170.jpeg",
    "MEDICAL DEVICES AND DIAGNOSTICS": "https://images.pexels.com/photos/8460085/pexels-photo-8460085.jpeg",
    "HAIR CARE": "https://images.pexels.com/photos/11482130/pexels-photo-11482130.jpeg",
    "TRAVEL": "https://images.pexels.com/photos/346885/pexels-photo-346885.jpeg",
    "MENS": "https://admin.qpharmpharmacy.com/storage/products/FLiUnkZrXBv1daORTbQ671YPCqnhRprjmEMRPAKG.jpg",
    "FAMILY PLANNING": "https://images.pexels.com/photos/5974436/pexels-photo-5974436.jpeg",
    "PETS": "https://images.pexels.com/photos/45170/kittens-cat-cat-puppy-rush-45170.jpeg",
    "SKIN CARE AND TREATMENTS": "https://images.pexels.com/photos/7281636/pexels-photo-7281636.jpeg",
    "FACIAL": "https://images.pexels.com/photos/5938658/pexels-photo-5938658.jpeg",
    "SUPPLEMENTS": "https://images.pexels.com/photos/208518/pexels-photo-208518.jpeg",
    "SERVICES": "https://images.pexels.com/photos/1181275/pexels-photo-1181275.jpeg",
  };

  if (loading) {
    return (
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 text-center text-lg text-gray-500">Loading categories...</div>
      </section>
    )
  }
  if (error) {
    return (
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 text-center text-red-500">{error}</div>
      </section>
    )
  }

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Shop by Category</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Browse through our wide range of health and wellness products, carefully curated for your needs.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {categories.map((category) => {
            // Try to find an image by matching category name (case-insensitive, trimmed)
            let imageSrc = category.image;
            if (!imageSrc) {
              // Try to match by category.category or category.name
              const catName = (category.category || category.name || "").trim().toUpperCase();
              imageSrc = categoryImages[catName] || "/banners/no-img.jpg";
            }
            return (
              <Link key={category.id} href={category.href}>
                <Card className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  <CardContent className="p-6 text-center">
                    <div className="mb-4 overflow-hidden rounded-lg">
                      <Image
                        src={imageSrc}
                        alt={category.category}
                        width={200}
                        height={200}
                        className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = "/banners/no-img.jpg";
                        }}
                      />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">
                      {category.category}
                    </h3>
                    <p className="text-sm text-gray-500">{category.count}</p>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  )
}
